import { useCrudService } from 'framework/crud';
import { useKernelApi } from 'infrastructure/api';
import { DATE_FORMAT, downloadFile, useDateService } from 'framework/utils';
import { Moment } from 'moment';

const LEAD_REPORT_API_PATH = '/crm/sale/report/lead';

export const LEAD_REPORT_PERMISSION = 'COMPANY_GENERATE_LEAD_REPORT';
export const LEAD_RAW_DATA_REPORT_PERMISSION = 'LEAD_DOWNLOAD_RAW_LEAD_DATA_REPORT';

export const PERIOD_TYPES = {
  CURRENT_MONTH: 'Current Month',
  LAST_MONTH: 'Last Month',
  CURRENT_YEAR: 'Current Year',
  LAST_YEAR: 'Last Year',
};

const useLeadReportService = () => {
  const { create: baseCreate, ...baseCrud } = useCrudService({
    singleResourceUrl: `${LEAD_REPORT_API_PATH}/:id`,
    listResourceUrl: LEAD_REPORT_API_PATH,
    useService: useKernelApi,
  });
  const { getCurrentDate, formatDate } = useDateService();

  const { sendGetRequest } = useKernelApi();

  const create = async (request: any) => {
    const { periodType } = request;
    const periodRange = getPeriodRange(periodType);
    return baseCreate({
      ...periodRange,
      filter: null,
    });
  };

  const getPeriodRange = (periodType: string) => {
    const currentDate = getCurrentDate();
    let periodFrom: Moment;
    let periodTo: Moment;
    switch (periodType) {
      case 'CURRENT_MONTH':
        periodFrom = currentDate.clone().startOf('month');
        periodTo = currentDate.clone().endOf('day');
        break;
      case 'LAST_MONTH':
        periodFrom = currentDate.clone().subtract(1, 'month').startOf('month');
        periodTo = currentDate.clone().subtract(1, 'month').endOf('month');
        break;
      case 'CURRENT_YEAR':
        periodFrom = currentDate.clone().startOf('year');
        periodTo = currentDate.clone().endOf('day');
        break;
      case 'LAST_YEAR':
        periodFrom = currentDate.clone().subtract(1, 'year').startOf('year');
        periodTo = currentDate.clone().subtract(1, 'year').endOf('year');
        break;
      default:
        throw new Error(`Unknown period type: ${periodType}`);
    }

    return {
      periodFrom: formatDate(periodFrom, DATE_FORMAT.API_ONLY_DATE)?.trim(),
      periodTo: formatDate(periodTo, DATE_FORMAT.API_ONLY_DATE)?.trim(),
    };
  };

  const downloadReport = async (id: string) => {
    const response = await sendGetRequest(`${LEAD_REPORT_API_PATH}/${id}`);
    downloadFile(response);
  };

  const downloadRawDataReport = async (fromDate: string, toDate: string) => {
    const queryParams = new URLSearchParams({
      fromDate,
      toDate,
    });
    const response = await sendGetRequest(`${LEAD_REPORT_API_PATH}/raw-data?${queryParams.toString()}`);
    downloadFile(response);
  };

  return {
    create,
    downloadReport,
    downloadRawDataReport,
    ...baseCrud,
  };
};

export default useLeadReportService;
